/**
 * SensitiveField — engine-aware field for AES-256-encrypted values (SSN, Routing#,
 * Account#, Pay Rate, etc.).
 *
 * Renders a redacted display by default. If the user has the configured HR sub-area
 * grant, a "Reveal" button calls the audited backend endpoint to fetch plaintext —
 * which auto-hides after a configurable timeout. If the user also has the edit
 * capability on the parent module, an "Edit" button swaps to an input.
 *
 * Backend remains the authoritative gate:
 *   • The reveal endpoint requires the HR grant
 *   • The set/update endpoint requires the HR grant + edit cap
 * This component just keeps the UI honest so users don't see buttons that 403.
 *
 *   <SensitiveField
 *     testId="employee-ssn"
 *     label="Social Security Number"
 *     module="employees"
 *     hrGrant="confidential"
 *     redactedValue={emp.ssn_last_4 ? `•••-••-${emp.ssn_last_4}` : null}
 *     placeholder="— not on file —"
 *     reveal={() => employeesAPI.revealSsn(id).then(r => r.data?.ssn)}
 *     save={(val) => employeesAPI.setSsn(id, val)}
 *     clear={() => employeesAPI.clearSsn(id)}
 *     format="ssn"            // optional: applies SSN mask on input
 *   />
 */
import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Pencil, Save, X, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions';

const formatters = {
  ssn: (v) => {
    const digits = (v || '').replace(/\D/g, '').slice(0, 9);
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  },
  routing: (v) => (v || '').replace(/\D/g, '').slice(0, 9),
  account: (v) => (v || '').replace(/\D/g, '').slice(0, 17),
  currency: (v) => {
    if (typeof v === 'number') return v.toFixed(2);
    // strip non-numeric except `.`
    const s = (v || '').toString().replace(/[^\d.]/g, '');
    const parts = s.split('.');
    return parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : s;
  },
};

export default function SensitiveField({
  testId,
  label,
  description,
  module,
  hrGrant,
  redactedValue,
  placeholder = '— not on file —',
  reveal,
  save,
  clear,
  format,
  autoHideMs = 30000,
  // Hide entire component when user lacks even view cap on the module.
  hideOnNoView = true,
}) {
  const perms = useEffectivePermissions();
  const [revealedValue, setRevealedValue] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const hideTimerRef = useRef(null);

  // Clear timer on unmount
  useEffect(() => () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); }, []);

  // Wait for perms to load before rendering — avoid flashing reveal/edit buttons.
  if (!perms.loaded && perms.role !== 'admin') return null;

  const canView = perms.can(module, 'view') || perms.role === 'admin';
  const canEdit = perms.can(module, 'edit') || perms.role === 'admin';
  // Admin gets every HR grant implicitly.
  const canReveal = !!reveal && (perms.role === 'admin' || (hrGrant ? perms.hasGrant(hrGrant) : true));
  const canMutate = !!save && canEdit && (perms.role === 'admin' || (hrGrant ? perms.hasGrant(hrGrant) : true));
  const hasValue = !!redactedValue;

  if (hideOnNoView && !canView) return null;

  const applyFormat = format ? (formatters[format] || ((x) => x)) : ((x) => x);

  const onReveal = async () => {
    setRevealing(true);
    try {
      const v = await reveal();
      if (v === null || v === undefined || v === '') {
        toast.info('No value on file');
        return;
      }
      setRevealedValue(v);
      if (autoHideMs > 0) {
        hideTimerRef.current = setTimeout(() => setRevealedValue(null), autoHideMs);
      }
      toast.success(`${label} revealed${autoHideMs > 0 ? ` — auto-hides in ${Math.round(autoHideMs / 1000)}s` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to reveal ${label}`);
    } finally {
      setRevealing(false);
    }
  };

  const onHide = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    setRevealedValue(null);
  };

  const startEdit = () => {
    setDraft(revealedValue || '');
    setEditing(true);
  };

  const onSave = async () => {
    if (!draft || !draft.trim()) {
      toast.error('Please enter a value');
      return;
    }
    setSaving(true);
    try {
      await save(draft.trim());
      toast.success(`${label} saved`);
      setEditing(false);
      setDraft('');
      // Leave revealed value cleared so the parent will re-load redacted state.
      setRevealedValue(null);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to save ${label}`);
    } finally {
      setSaving(false);
    }
  };

  const onClear = async () => {
    if (!clear) return;
    if (!confirm(`Clear ${label}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await clear();
      toast.success(`${label} cleared`);
      setRevealedValue(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to clear ${label}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}

      {editing ? (
        <div className="flex items-center gap-2">
          <Input
            value={applyFormat(draft)}
            onChange={(e) => setDraft(applyFormat(e.target.value))}
            className="font-mono"
            placeholder={placeholder}
            autoFocus
            data-testid={`${testId}-input`}
          />
          <Button size="sm" type="button" onClick={onSave} disabled={saving} data-testid={`${testId}-save`}>
            <Save className="w-3.5 h-3.5 mr-1" /> Save
          </Button>
          <Button size="sm" variant="outline" type="button" onClick={() => { setEditing(false); setDraft(''); }} data-testid={`${testId}-cancel`}>
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Input
            value={revealedValue || redactedValue || placeholder}
            readOnly
            className={`font-mono ${!hasValue && !revealedValue ? 'text-slate-400' : ''}`}
            data-testid={`${testId}-display`}
          />
          {hasValue && canReveal && (revealedValue ? (
            <Button size="sm" variant="outline" type="button" onClick={onHide} data-testid={`${testId}-hide`}>
              <EyeOff className="w-3.5 h-3.5 mr-1" /> Hide
            </Button>
          ) : (
            <Button size="sm" variant="outline" type="button" disabled={revealing} onClick={onReveal} data-testid={`${testId}-reveal`}>
              <Eye className="w-3.5 h-3.5 mr-1" /> {revealing ? '…' : 'Reveal'}
            </Button>
          ))}
          {canMutate && (
            <Button size="sm" variant="outline" type="button" onClick={startEdit} data-testid={`${testId}-edit`}>
              <Pencil className="w-3.5 h-3.5 mr-1" /> {hasValue ? 'Edit' : 'Add'}
            </Button>
          )}
          {canMutate && clear && hasValue && (
            <Button size="sm" variant="outline" type="button" onClick={onClear} disabled={saving}
                    className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`${testId}-clear`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      )}

      {/* Tiny status footnote */}
      <p className="text-xs text-slate-500">
        {description || (
          canReveal
            ? 'Every reveal is audit-logged. Encrypted at rest (AES-256).'
            : `View-only. ${hrGrant ? `Requires "${hrGrant}" HR grant to reveal.` : 'Reveal not available for your role.'}`
        )}
      </p>
    </div>
  );
}
